import React from 'react';
import { Flex, Menu, Text } from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import { Icon as ChakraIcon } from '@chakra-ui/react';
import { Calendar, Globe } from 'lucide-react';
import { FilterHeading } from 'src/components/atoms/FilterHeading';
import { FilterAccordion } from 'src/components/molecules/FilterAccordion';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuOpenerButton } from 'src/components/atoms/MenuOpenerButton';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { MenuTriangle } from 'src/components/atoms/MenuTriangle';
import { MenuList } from 'src/components/molecules/MenuList';
import { SosLocation } from 'src/api/brandTracking';
import { TimeRangeMonths } from '../../../utils/shareOfSearch';

const COUNTRY_BUTTON_NAME = 'brand-tracking-country-select';
const TIME_RANGE_BUTTON_NAME = 'brand-tracking-time-range-select';

const TIME_RANGE_OPTIONS: Array<{ label: string; value: TimeRangeMonths }> = [
  { label: 'Last 12 months', value: 12 },
  { label: 'Last 24 months', value: 24 },
  { label: 'Last 48 months', value: 48 },
  { label: 'All time', value: null },
];

type BrandTrackingFilterBarProps = {
  countries: SosLocation[];
  selectedLocationId: number | null;
  onLocationChange: (locationId: number) => void;
  timeRange: TimeRangeMonths;
  onTimeRangeChange: (timeRange: TimeRangeMonths) => void;
};

// The shared TimeRangeSelector is wired to the model-bound FilterContext,
// which this model-independent page doesn't have — hence local menus built
// from the same atoms (MenuOpenerButton/MenuButton/MenuList/MenuOption)
// every other filter bar uses.
export const BrandTrackingFilterBar: React.FC<BrandTrackingFilterBarProps> = ({
  countries,
  selectedLocationId,
  onLocationChange,
  timeRange,
  onTimeRangeChange,
}) => {
  const selectedCountry =
    countries.find((c) => c.locationId === selectedLocationId) ?? countries[0];

  const selectedTimeRange =
    TIME_RANGE_OPTIONS.find((option) => option.value === timeRange) ??
    TIME_RANGE_OPTIONS[1];

  return (
    <FilterAccordion>
      <Flex w="100%" alignItems="center" wrap="wrap">
        {/* Country */}
        <Flex direction="column" alignItems="flex-start" mr={3} my={2}>
          <FilterHeading headingText="Country" color="content.label" />
          <MenuOpenerButton>
            <Menu isLazy>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    buttonName={COUNTRY_BUTTON_NAME}
                    color="content.label"
                    fontSize="md"
                    fontFamily="marketingPage"
                    fontWeight="normal"
                    bg="transparent"
                    p={0}
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}>
                    <Flex alignItems="center">
                      <ChakraIcon
                        as={Globe}
                        boxSize={5}
                        mr={2}
                        color="content.title"
                        verticalAlign="middle"
                      />
                      <Text
                        fontSize="16px"
                        fontFamily="marketingPage"
                        fontWeight={500}
                        color="content.title">
                        {selectedCountry?.locationName ?? '—'}
                      </Text>
                      <MenuTriangle isOpen={isOpen} />
                    </Flex>
                  </MenuButton>
                  <MenuList buttonName={COUNTRY_BUTTON_NAME}>
                    {countries.map((country) => (
                      <MenuOption
                        key={country.locationId}
                        buttonName={COUNTRY_BUTTON_NAME}
                        label={country.locationName}
                        value={country.locationId}
                        isSelected={
                          country.locationId === selectedCountry?.locationId
                        }
                        onClick={() => onLocationChange(country.locationId)}
                      />
                    ))}
                  </MenuList>
                </>
              )}
            </Menu>
          </MenuOpenerButton>
        </Flex>

        {/* Time Range */}
        <Flex direction="column" alignItems="flex-start" mr={3} my={2}>
          <FilterHeading headingText="Time Range" color="content.label" />
          <MenuOpenerButton>
            <Menu isLazy>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    buttonName={TIME_RANGE_BUTTON_NAME}
                    color="content.label"
                    fontSize="md"
                    fontFamily="marketingPage"
                    fontWeight="normal"
                    bg="transparent"
                    p={0}
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}>
                    <Flex alignItems="center">
                      <ChakraIcon
                        as={Calendar}
                        boxSize={5}
                        mr={2}
                        color="content.title"
                        verticalAlign="middle"
                      />
                      <Text
                        fontSize="16px"
                        fontFamily="marketingPage"
                        fontWeight={500}
                        color="content.title">
                        {selectedTimeRange.label}
                      </Text>
                      <MenuTriangle isOpen={isOpen} />
                    </Flex>
                  </MenuButton>
                  <MenuList buttonName={TIME_RANGE_BUTTON_NAME}>
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <MenuOption
                        key={option.label}
                        buttonName={TIME_RANGE_BUTTON_NAME}
                        label={option.label}
                        value={option.label}
                        isSelected={option.value === timeRange}
                        onClick={() => onTimeRangeChange(option.value)}
                      />
                    ))}
                  </MenuList>
                </>
              )}
            </Menu>
          </MenuOpenerButton>
        </Flex>
      </Flex>
    </FilterAccordion>
  );
};
